import { useMemo, useState, useRef } from 'react';
import { Group, createStyles } from '@mantine/core';
import { z } from 'zod';
import useTranslation from 'next-translate/useTranslation';

import dt from '~/domains/contest/domains/managev2/domains/common/testing.const';
import { Text } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';

import type { FieldDescriptor, StaticFieldProps } from '../../types/common';

import type {
  APISegmentsFieldType,
  SegmentsFieldItemType,
  SegmentsFieldType,
  PayoutSegmentsValue,
  SegmentsEditability,
} from './types';
import { SegmentsEdit } from './SegmentsEdit/SegmentsEdit';
import { SegmentsView } from './SegmentsView';
import { CustomSegmentsEdit } from './CustomSegmentsEdit/CustomSegmentsEdit';

function getCanEdit(
  isLocked: boolean,
  isCustom: boolean,
  editability?: SegmentsEditability
): { canEditAnything: boolean; canEditLabels: boolean; canEditSlates: boolean } {
  if (isLocked) {
    return { canEditAnything: false, canEditLabels: false, canEditSlates: false };
  }

  if (!editability) {
    return { canEditAnything: true, canEditLabels: isCustom, canEditSlates: true };
  }

  // Labels can only be edited if isCustom is true AND editability allows it
  const canEditLabels = isCustom && editability.labels !== false;
  const canEditSlates = editability.slateAssignments !== false;
  const canEditAnything = canEditLabels || canEditSlates;

  return { canEditAnything, canEditLabels, canEditSlates };
}

const useStyles = createStyles((theme) => ({
  heading: {
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    padding: `${theme.spacing.xs / 2}px 0`,
  },
  button: {
    marginTop: theme.spacing.md,
  },
}));

function SegmentsFieldElement({
  field,
  value,
  onValueChange,
}: StaticFieldProps<SegmentsFieldType>) {
  const { classes } = useStyles();
  const { t } = useTranslation('contest');
  const [isEditing, setIsEditing] = useState(false);
  const isCustom = !!field.isCustom;
  const isLocked = !!field.locked;
  const segmentsContainerRef = useRef<HTMLDivElement>(null);

  const { canEditAnything, canEditLabels, canEditSlates } = getCanEdit(
    isLocked,
    isCustom,
    field.editability
  );

  const segmentsValue = (value as PayoutSegmentsValue) ?? field.defaultValue;

  const segments = useMemo(
    () =>
      Object.entries(segmentsValue)
        .sort(([, a], [, b]) => a.sortOrder - b.sortOrder)
        .map(([id, segment]) => ({
          key: id,
          index: segment.sortOrder,
          label: segment.label,
          items: segment.slateGroupIds
            .map((slateId) => field.slates.find((item) => item.key === slateId))
            .filter(Boolean),
        })),
    [field.slates, segmentsValue]
  );

  const handleToggleEdit = () => {
    setIsEditing((v) => !v);
    setTimeout(() => {
      segmentsContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  return (
    <div ref={segmentsContainerRef} data-test-id={dt.segments.container}>
      <Group position="apart" className={classes.heading}>
        <Text variant="body-large">{field.label}</Text>
      </Group>
      {isEditing && canEditAnything ? (
        <>
          <SegmentsEditField
            field={field}
            value={value}
            onValueChange={onValueChange}
            isCustom={isCustom}
            canEditLabels={canEditLabels}
            canEditSlates={canEditSlates}
          />
          <Button
            className={classes.button}
            variant="primary-fill"
            size="medium"
            fullWidth
            data-test-id={dt.segments.action.save}
            onClick={handleToggleEdit}
          >
            <Text variant="label-medium">Save segments</Text>
          </Button>
        </>
      ) : (
        <>
          <SegmentsView segments={segments} slateLabel={field.slateLabel} />
          {canEditAnything && (
            <Button
              className={classes.button}
              variant="tertiary-fill"
              size="medium"
              fullWidth
              data-test-id={dt.segments.action.edit}
              onClick={handleToggleEdit}
            >
              <Text variant="label-medium">Customize segments</Text>
            </Button>
          )}
        </>
      )}
    </div>
  );
}

function SegmentsEditField({
  field,
  value,
  onValueChange,
  isCustom,
  canEditLabels,
  canEditSlates,
}: StaticFieldProps<SegmentsFieldType> & {
  isCustom: boolean;
  canEditLabels: boolean;
  canEditSlates: boolean;
}) {
  if (isCustom && !field.segmentLabelField) {
    console.error('CustomSegmentsEdit requires segmentLabelField to be provided');
    return null;
  }

  const segmentsValue = (value as PayoutSegmentsValue) ?? field.defaultValue;

  return isCustom ? (
    <CustomSegmentsEdit
      slates={field.slates}
      rules={field.rules}
      value={segmentsValue}
      onChange={onValueChange}
      slateLabel={field.slateLabel}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      segmentLabelField={field.segmentLabelField!}
      canEditLabels={canEditLabels}
      canEditSlates={canEditSlates}
    />
  ) : (
    <SegmentsEdit
      slates={field.slates}
      rules={field.rules}
      value={segmentsValue}
      onChange={onValueChange}
      slateLabel={field.slateLabel}
      canEditSlates={canEditSlates}
    />
  );
}

const Descriptor: FieldDescriptor<APISegmentsFieldType, SegmentsFieldType> = {
  FieldComponent: SegmentsFieldElement,
  name: 'SegmentsField',
  parseFieldResponse: ({ field, fieldKey }) => {
    const slates: SegmentsFieldItemType[] = field.slates.map((slate) => ({
      ...slate,
      date: new Date(slate.date),
    }));

    return [
      {
        key: fieldKey,
        type: field.type,
        data: {
          ...field,
          slates,
        },
      },
    ];
  },
  createValidationSchema: (field) => {
    const segmentSchema = z.object({
      id: z.string(),
      label: z.string().min(1).max(20),
      slateGroupIds: z.array(z.string()).min(field.rules.minSlateGroupsPerSegment, {
        message: `Each segment must have at least ${field.rules.minSlateGroupsPerSegment} slate groups`,
      }),
      sortOrder: z.number(),
    });

    return z
      .record(z.string(), segmentSchema)
      .refine((segments) => Object.keys(segments).length > 0, {
        message: 'At least one segment is required',
      })
      .refine(
        (segments) => {
          const labels = Object.values(segments).map((s) => s.label);
          return labels.length === new Set(labels).size;
        },
        { message: 'Segment names must be unique' }
      );
  },
};

export default Descriptor;
